/**
 * Permission Hooks
 * Utility hooks for UI-based permission checks
 * Use these to conditionally render buttons, actions, exports, etc.
 */

import { useAuth } from '@/providers/AuthProvider';

/**
 * Hook to check if user can export data
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanExport(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin']);
}

/**
 * Hook to check if user can create assessments
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanCreateAssessments(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin']);
}

/**
 * Hook to check if user can delete assessments
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanDeleteAssessments(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin']);
}

/**
 * Hook to check if user can edit assessments
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanEditAssessments(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin']);
}

/**
 * Hook to check if user can CREATE/EDIT/DELETE properties
 * super_admin: YES ✅ (full CRUD)
 * admin: NO ❌ (view-only)
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanManageProperties(): boolean {
  const { hasRole } = useAuth();
  return hasRole('super_admin');
}

/**
 * Hook to check if user can CREATE/EDIT/DELETE templates
 * super_admin: YES ✅ (full CRUD)
 * admin: NO ❌ (view-only)
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanManageTemplates(): boolean {
  const { hasRole } = useAuth();
  return hasRole('super_admin');
}




/**
 * Hook to check if user is auditor (restricted view)
 */
export function useIsAuditor(): boolean {
  const { hasRole } = useAuth();
  return hasRole('auditor');
}

/**
 * Hook to check if user is manager (restricted view)
 */
export function useIsManager(): boolean {
  const { hasRole } = useAuth();
  return hasRole('manager');
}

/**
 * Hook to check if user has admin privileges (super_admin or admin)
 */
export function useIsAdminOrAbove(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin']);
}

/**
 * Check if user can show ACTION COLUMNS in tables
 * Actions include: Edit, Delete, Perform (for manager)
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌ (cannot see Actions column)
 * auditor: YES ✅ (can see Perform action only)
 */
export function useCanShowActions(): boolean {
  const { hasAnyRole } = useAuth();
  // Super admin and admin can see all actions
  // Auditor can see perform action
  // Manager cannot see any actions
  return hasAnyRole(['super_admin', 'admin', 'auditor']);
}

/**
 * Check if user can show FILTERS in assessment lists
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: YES ✅
 * auditor: NO ❌ (only sees assigned assessments, no filtering needed)
 */
export function useCanShowFilters(): boolean {
  const { hasRole } = useAuth();
  return !hasRole('auditor'); // All except auditor
}

/**
 * Check if user can access PERFORM page for assessments
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: YES ✅ (view-only access)
 * auditor: YES ✅ (can perform assigned assessments)
 */
export function useCanPerformAssessments(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin', 'manager', 'auditor']);
}

/**
 * Check if user can EDIT assessments (not just view)
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌ (view-only)
 * auditor: YES ✅
 */
export function useCanEditPerformPage(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin', 'auditor']);
}

/**
 * Check if user can see export buttons in property detail page
 * super_admin: YES ✅
 * admin: YES ✅
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanExportFromPropertyPage(): boolean {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(['super_admin', 'admin']);
}

/**
 * Check if user can CREATE/EDIT/DELETE users
 * super_admin: YES ✅ (full CRUD)
 * admin: NO ❌ (view-only)
 * manager: NO ❌
 * auditor: NO ❌
 */
export function useCanManageUsers(): boolean {
  const { hasRole } = useAuth();
  return hasRole('super_admin');
}

/**
 * Get permission summary object for easy destructuring
 */
export function usePermissions() {
  return {
    canExport: useCanExport(),
    canCreateAssessments: useCanCreateAssessments(),
    canDeleteAssessments: useCanDeleteAssessments(),
    canEditAssessments: useCanEditAssessments(),
    canManageProperties: useCanManageProperties(),
    canManageTemplates: useCanManageTemplates(),
    canManageUsers: useCanManageUsers(),
    canShowActions: useCanShowActions(),
    canShowFilters: useCanShowFilters(),
    canPerformAssessments: useCanPerformAssessments(),
    canEditPerformPage: useCanEditPerformPage(),
    canExportFromPropertyPage: useCanExportFromPropertyPage(),
    isAuditor: useIsAuditor(),
    isManager: useIsManager(),
    isAdminOrAbove: useIsAdminOrAbove(),
  };
}
